import { useQuery } from '@tanstack/react-query'
import { fetchBillingStatus } from '@/api/billing'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useHousehold } from '@/hooks/useHousehold'
import { HERO_SHELL } from '@/components/loans/loanHero'
import { cn } from '@/lib/utils'
import { formatBillingDate, planSummary } from '@/lib/planStatus'

const FY_SHORT: Record<1 | 4, string> = { 4: 'FY Apr–Mar', 1: 'FY Jan–Dec' }

/** The household at a glance: whose it is, what plan it's on, how money and years are counted. */
export function SettingsHero() {
  const { data: household } = useHousehold()
  const { role } = useCurrentHousehold()
  const { data: billing } = useQuery({ queryKey: ['billing-status'], queryFn: fetchBillingStatus })

  if (!household) return null

  const plan =
    billing?.status === 'sample' ? 'Sample household — free' : billing ? planSummary(billing) : null
  const money = `${household.currency} · ${FY_SHORT[(household.financialYearStartMonth ?? 4) as 1 | 4]}`
  const people = `${household.userCount} ${household.userCount === 1 ? 'user' : 'users'} · ${household.trackedPeopleCount} tracked`
  const facts = [
    plan && { label: 'Plan', value: plan },
    { label: 'Money in', value: money },
    { label: 'People', value: people },
  ].filter((f): f is { label: string; value: string } => !!f)

  return (
    <section
      aria-label="Your household"
      className={cn(
        HERO_SHELL,
        'flex flex-col gap-1 motion-safe:animate-fade-slide-up md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-5'
      )}
    >
      <div className="min-w-0">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-[11.5px]">
          Your household
        </p>
        <p className="truncate font-serif text-xl font-semibold leading-tight md:mt-1 md:text-[28px]">
          {household.name}
        </p>
        <p className="mt-0.5 hidden text-[13px] text-[var(--fl-hero-fg-2)] md:block">
          {role === 'OWNER' ? "You're the owner" : "You're a member"} · since{' '}
          {formatBillingDate(household.createdAt)}
        </p>
        {/* Phone: one line instead of the side facts. */}
        <p className="mt-0.5 text-xs text-[var(--fl-hero-fg-2)] md:hidden">
          {[plan, money].filter(Boolean).join(' · ')}
        </p>
      </div>
      <dl className="hidden gap-7 text-[13px] md:flex">
        {facts.map((f) => (
          <div key={f.label}>
            <dt className="text-xs text-[var(--fl-hero-fg-2)]">{f.label}</dt>
            <dd className="mt-0.5 font-semibold">{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
